/**
 * Portal footer displaying support email, legal links, and the language
 * switcher.
 *
 * @module components/Footer
 */

import { useTranslation } from 'react-i18next';
import type { applicant } from '@noxverify/api-contracts';
import {
  LOCALE_METADATA,
  isSupportedLocale,
  type SupportedLocale,
} from '@noxverify/i18n';
import { LanguageSwitcher } from './LanguageSwitcher';

interface FooterProps {
  branding: applicant.ApplicantBranding;
}

export function Footer({ branding }: FooterProps) {
  const { t, i18n } = useTranslation('portal');
  const hasLinks = branding.termsUrl || branding.privacyUrl;
  const hasSupport = branding.supportEmail;

  const activeLocale: SupportedLocale = isSupportedLocale(i18n.language)
    ? (i18n.language as SupportedLocale)
    : 'en';
  const activeName = LOCALE_METADATA[activeLocale]?.nativeName ?? 'English';

  return (
    <footer className="border-t border-hairline bg-surface py-8">
      <div className="mx-auto max-w-2xl px-5 text-center text-sm text-ink-muted">
        {hasSupport && (
          <p className="mb-3">
            {t('footer.needHelp')}{' '}
            <a
              href={`mailto:${branding.supportEmail}`}
              className="font-medium text-primary underline-offset-2 hover:underline"
            >
              {branding.supportEmail}
            </a>
          </p>
        )}
        {hasLinks && (
          <nav
            aria-label={t('footer.legalLinks', { defaultValue: 'Legal links' })}
            className="flex items-center justify-center gap-5"
          >
            {branding.termsUrl && (
              <a
                href={branding.termsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline-offset-2 hover:underline"
              >
                {t('footer.termsOfService')}
              </a>
            )}
            {branding.privacyUrl && (
              <a
                href={branding.privacyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline-offset-2 hover:underline"
              >
                {t('footer.privacyPolicy')}
              </a>
            )}
          </nav>
        )}

        <div className="mt-5 flex flex-col items-center justify-center gap-2 sm:flex-row sm:gap-4">
          <LanguageSwitcher />
          <span
            className="font-mono text-xs uppercase tracking-[0.08em] text-ink-muted"
            aria-live="polite"
          >
            {t('footer.language', { defaultValue: 'Language' })}: {activeName}
          </span>
        </div>
      </div>
    </footer>
  );
}
